import { ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { MultiSelectFilter } from '@zendesk/forethought-elements';
import FilterLabelContainer from './FilterLabelContainer';
import ListOption from './ListOption';
import SourceFilterOption from './SourceFilterOption';

type Option = ComponentProps<typeof MultiSelectFilter>['options'][number];

interface ListOptionParentProps {
  expandedValues: string[];
  // Optional trailing options rendered under the same "Others" group but backed
  // by their own value/onChange (e.g. the Human feedback filter, whose state is
  // shared with the Flagged toggle button rather than the intent-filter list).
  // Rendered after the sorted options so they always appear last.
  extraOptions?: Option[];
  extraValue?: string[];
  onChange: (value: string[]) => void;
  onExtraChange?: (value: string[]) => void;
  onSourceFilterChange?: (value: string[]) => void;
  options: Option[];
  preserveOrder?: boolean;
  setExpandedValues: (value: string[]) => void;
  sourceFilter?: string[];
  sourceFilterOptions?: Array<{ label: string; value: string }>;
  value: string[];
}

const SOURCE_FILTER_KEY = '__source__';

const ListOptionParent = ({
  expandedValues,
  extraOptions,
  extraValue,
  onChange,
  onExtraChange,
  onSourceFilterChange,
  options,
  preserveOrder = false,
  setExpandedValues,
  sourceFilter,
  sourceFilterOptions,
  value,
}: ListOptionParentProps) => {
  const { t } = useTranslation('discover');
  const showSourceFilter =
    sourceFilterOptions &&
    sourceFilterOptions.length > 1 &&
    onSourceFilterChange &&
    sourceFilter !== undefined;

  // Create a sorted list of items that includes both regular options and source filter
  const sortedItems = useMemo(() => {
    const items: Array<{ option?: Option; type: 'option' | 'source' }> =
      options.map(option => ({ option, type: 'option' as const }));

    if (showSourceFilter) {
      items.push({ type: 'source' as const });
    }

    if (preserveOrder) {
      return items;
    }

    // Sort alphabetically by label (Source filter uses "Source" as its label)
    return items.sort((a, b) => {
      const labelA =
        a.type === 'source'
          ? t('sourceFilterOption.source')
          : (a.option?.label ?? '');
      const labelB =
        b.type === 'source'
          ? t('sourceFilterOption.source')
          : (b.option?.label ?? '');
      return labelA.localeCompare(labelB);
    });
  }, [options, preserveOrder, showSourceFilter, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <FilterLabelContainer label={t('listOptionParent.othersLabel')}>
        <Box
          sx={{
            marginTop: '8px',
          }}
        >
          {sortedItems.map(item => {
            if (
              item.type === 'source' &&
              onSourceFilterChange &&
              sourceFilterOptions &&
              sourceFilter
            ) {
              return (
                <SourceFilterOption
                  expandedValues={expandedValues}
                  key={SOURCE_FILTER_KEY}
                  onChange={onSourceFilterChange}
                  setExpandedValues={setExpandedValues}
                  sourceFilterOptions={sourceFilterOptions}
                  value={sourceFilter}
                />
              );
            }
            if (item.type === 'option' && item.option) {
              return (
                <ListOption
                  expandedValues={expandedValues}
                  isOuter={true}
                  item={item.option}
                  key={item.option.value}
                  onChange={onChange}
                  setExpandedValues={setExpandedValues}
                  value={value}
                />
              );
            }
            return null;
          })}
          {extraOptions?.map(option => (
            <ListOption
              expandedValues={expandedValues}
              isOuter={true}
              item={option}
              key={option.value}
              onChange={onExtraChange ?? (() => undefined)}
              setExpandedValues={setExpandedValues}
              value={extraValue ?? []}
            />
          ))}
        </Box>
      </FilterLabelContainer>
    </Box>
  );
};

export default ListOptionParent;
